import { ProductAttribute } from "@wttb/product-configurator";
import cn from "clsx";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { ChoiceButton } from "@/common/components/form/choice-button";
import { Input } from "@/common/components/form/input";
import { FlagType } from "@/features/product/components/product-attributes/product-attributes-renderer";
import { useStoreProduct } from "@/features/product/contexts/product-context";

export type SingleOption = {
  id: string;
  name: string;
  value: string;
  flags?: FlagType[];
  disabled: boolean;
  isIncompatible?: boolean;
};

type SingleChoiceButtonSelectorTypes = {
  options: SingleOption[];
  attribute: ProductAttribute;
};

export function VersionsAttribute({ options, attribute }: SingleChoiceButtonSelectorTypes) {
  const t = useTranslations("custom-attributes-forms");

  const { config, selectedOptions, setCustomConfigValue, setConfigValue } = useStoreProduct();
  const {
    watch,
    resetField,
    setValue,
    register,
    getValues,
    formState: { errors },
  } = useForm<{ versions?: number }>({
    mode: "onChange",
  });

  useEffect(() => {
    // Check if versions are specified at the URL params on first load
    if (!selectedOptions?.versions) return;

    const isOptionExists = !!options.find(({ value }) => value === selectedOptions.versions);
    if (isOptionExists) return;

    // If there is no predefined option for the selected versions value,
    // we set it as a custom number of versions (with revalidation)
    setValue("versions", selectedOptions?.versions as number, { shouldValidate: true });
  }, []);

  const versions = watch("versions");

  const handleChange = (valueId: string = "") => {
    if (valueId && typeof valueId === "string") {
      setConfigValue(attribute.key, valueId);
      resetField("versions");
    } else {
      setCustomConfigValue(attribute.key, getValues("versions") || 1);
    }
  };

  const max = attribute.max ?? 100;
  const min = attribute.min ?? 0;

  return (
    <>
      <div className="flex flex-col items-end gap-5 sm:flex-row">
        <div className="flex flex-1 flex-wrap gap-2 xl:gap-3">
          {options?.map((option: SingleOption) => (
            <ChoiceButton
              {...option}
              onInputChange={() => handleChange(option.id)}
              key={option.id}
              isChecked={option.value === config![attribute.key]}
              type="radio"
              disabled={option.disabled}
              styles="size-12 rounded-lg"
            />
          ))}
        </div>

        <div className="xs:text-xs xs:leading-none w-full sm:relative sm:w-[110px] xl:w-[120px]">
          <label className="text-sm leading-none sm:absolute sm:-top-full xl:-top-3/4">{`${t("label-custom-version")}`}</label>
          <Input
            {...register("versions", {
              onChange: handleChange,
              valueAsNumber: true,
              validate: {
                isLessThenMinimum: (v) =>
                  !Number.isInteger(v) || Number(v) > 0 || t("text-minimum-one"),
                inInteger: (v) =>
                  Number.isNaN(v) || Number.isInteger(v) || t("text-decimal-forbidden"),
                max: (v) => !Number.isInteger(v) || Number(v) <= max || t("error-max"),
                min: (v) => !Number.isInteger(v) || Number(v) >= min || t("error-min"),
              },
            })}
            variant="solid"
            type="number"
            inputClassName={cn({
              "!bg-berry-10 !border-[2px] !border-berry-90": !!versions,
            })}
            className="mt-3 rounded-lg sm:mt-0"
          />
          <div className="text-danger absolute min-h-[20px] pr-5 pt-1 text-xs">
            {errors.versions?.message}
          </div>
        </div>
      </div>
    </>
  );
}
